/**
 * ServiceApp-branded Google sign-in via Google Identity Services + signInWithIdToken.
 * Uses a dedicated Google OAuth client (configured in Platform Admin) so the Google
 * consent screen shows ServiceApp, not Tools & Bubbles.
 */
import { supabase } from '../../lib/supabase.js';
import { getAppMode } from '../appMode.js';
import { isSaasPlatformHost, isToolsAndBubblesHost } from '../../share/serviceappBrand.js';

/**
 * ServiceApp-branded GIS popup — on serviceapp.ai hosts and saas-platform builds
 * (including local `npm run dev:saas`). Never on toolsandbubbles.com.
 */
export function shouldUseServiceAppGoogleAuth(): boolean {
  if (typeof window === 'undefined') return false;
  if (isToolsAndBubblesHost(window.location.hostname)) return false;
  if (isSaasPlatformHost(window.location.hostname)) return true;
  return getAppMode() === 'saas-platform';
}

/**
 * Cursor Simple Browser / embedded shells often render a blank Google GIS page.
 * Prefer a real Chrome/Safari/Edge window for OAuth.
 */
export function isRestrictedOAuthBrowser(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const ua = navigator.userAgent || '';
  // Electron / IDE webviews without a normal Chrome brand token struggle with GIS.
  if (/Electron/i.test(ua) && !/\bEdg\/|\bChrome\/\d{2,}/i.test(ua)) return true;
  if (/Cursor/i.test(ua)) return true;
  return false;
}

/** Real browser URL to open when Google OAuth cannot run in the current shell. */
export function preferredGoogleAuthLoginUrl(): string {
  if (typeof window === 'undefined') return 'https://serviceapp.ai/login';
  const { protocol, hostname, port } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
  if (isLocal) {
    const host = port ? `${hostname}:${port}` : hostname;
    return `${protocol}//${host}/login`;
  }
  return 'https://serviceapp.ai/login';
}

const GSI_SCRIPT = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            ux_mode: 'popup' | 'redirect';
            callback: (response: { code?: string; error?: string }) => void;
            error_callback?: (error: { type?: string; message?: string }) => void;
          }) => { requestCode: () => void };
        };
      };
    };
  }
}

let cachedClientId: string | null | undefined;

export function loadGoogleIdentityScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google sign-in is only available in the browser.'));
  }
  if (window.google?.accounts?.oauth2) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SCRIPT}"]`) as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

/** Public ServiceApp Google Web Client ID (app_config or VITE_SAAS_GOOGLE_CLIENT_ID). */
export async function fetchSaasGoogleOAuthClientId(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_SAAS_GOOGLE_CLIENT_ID?.trim();
  if (fromEnv) return fromEnv;

  if (cachedClientId !== undefined) return cachedClientId;

  try {
    const res = await fetch('/api/saas/billing-status');
    if (!res.ok) {
      cachedClientId = null;
      return null;
    }
    const data = (await res.json()) as { google_oauth_client_id?: unknown };
    const id = typeof data.google_oauth_client_id === 'string' ? data.google_oauth_client_id.trim() : '';
    cachedClientId = id || null;
  } catch {
    cachedClientId = null;
  }
  return cachedClientId;
}

/** Opens Google popup (ServiceApp branding) and completes Supabase session via id_token. */
export async function signInWithServiceAppGoogle(clientId: string): Promise<void> {
  if (isRestrictedOAuthBrowser()) {
    throw new Error(
      'Google sign-in cannot run inside this preview. Open https://serviceapp.ai/login in Chrome or Safari, then try again.',
    );
  }

  await loadGoogleIdentityScript();

  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services failed to initialize.');
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    const client = window.google!.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      error_callback: (error) => {
        const message = error?.message || error?.type || 'Google sign-in failed to open.';
        finish(() => reject(new Error(message)));
      },
      callback: async (response) => {
        if (response.error) {
          finish(() => reject(new Error(response.error)));
          return;
        }
        if (!response.code) {
          finish(() => reject(new Error('Google sign-in was cancelled.')));
          return;
        }

        try {
          const exchangeRes = await fetch('/api/saas/google-oauth-exchange', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: response.code }),
          });
          const payload = (await exchangeRes.json()) as {
            id_token?: string;
            error?: string;
            message?: string;
          };
          if (!exchangeRes.ok || !payload.id_token) {
            finish(() =>
              reject(new Error(payload.message || payload.error || 'Google token exchange failed.')),
            );
            return;
          }

          // Auth-code id_tokens are not issued with our custom GIS nonce — omit nonce.
          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: payload.id_token,
          });
          if (error) {
            finish(() => reject(new Error(error.message || 'Google sign-in failed.')));
            return;
          }
          finish(() => resolve());
        } catch (err) {
          finish(() => reject(err instanceof Error ? err : new Error('Google sign-in failed.')));
        }
      },
    });

    client.requestCode();
  });
}
